import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  SKEL_GRADIENTS,
  TABLE_HEADER_SKELETONS,
  TABLE_ROW_SKELETON_KEYS,
  getSkeletonToggleBorderRadius,
  getSkeletonToggleMarginLeft,
  tdSx,
  thSx,
} from './shared';

const LoadingSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 1.5, background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', opacity: 0.5 }} />
        <Box>
          <Skeleton width={220} height={28} animation="wave" sx={{ borderRadius: 0.5 }} />
          <Skeleton width={280} height={14} animation="wave" sx={{ borderRadius: 0.5, mt: 0.25 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={105} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rounded" width={90} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
      </Box>
    </Box>

    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      {SKEL_GRADIENTS.map((gradient) => (
        <Card key={gradient} sx={{ flex: '1 1 0', minWidth: 180, background: gradient, color: 'white' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Skeleton width={90} height={14} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 0.5 }} />
              <Skeleton variant="circular" width={20} height={20} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
              <Skeleton width={40} height={38} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.35)', borderRadius: 0.5 }} />
              <Skeleton width={55} height={11} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 0.5 }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton width={120} height={10} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 0.5 }} />
              <Skeleton variant="rounded" width={36} height={18} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 9 }} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>

    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 0 }}>
        {[56, 62, 60, 52, 48].map((width, index, widths) => (
          <Skeleton
            key={`filter-skeleton-${width}`}
            variant="rounded"
            width={width}
            height={32}
            animation="wave"
            sx={{
              borderRadius: getSkeletonToggleBorderRadius(index, widths.length - 1),
              border: '1px solid #e0e0e0',
              ml: getSkeletonToggleMarginLeft(index),
            }}
          />
        ))}
      </Box>
      <Skeleton variant="rounded" width={200} height={36} animation="wave" sx={{ borderRadius: 1 }} />
    </Box>

    <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {TABLE_HEADER_SKELETONS.map((column) => (
                <TableCell key={column.key} sx={{ ...thSx, py: 1.2 }}>
                  <Skeleton width={column.width} height={10} animation="wave" sx={{ borderRadius: 0.5 }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {TABLE_ROW_SKELETON_KEYS.map((rowKey, rowIndex) => (
              <TableRow key={rowKey} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafbfc' } }}>
                <TableCell sx={tdSx}><Skeleton width={16} height={12} animation="wave" sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell sx={tdSx}><Skeleton width={36} height={14} animation="wave" sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell sx={tdSx}>
                  <Skeleton width={44} height={14} animation="wave" sx={{ borderRadius: 0.5 }} />
                  <Skeleton width={32} height={10} animation="wave" sx={{ borderRadius: 0.5, mt: 0.3 }} />
                </TableCell>
                <TableCell sx={tdSx}><Skeleton variant="rounded" width={52} height={22} animation="wave" sx={{ borderRadius: 1 }} /></TableCell>
                <TableCell sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Skeleton variant="circular" width={14} height={14} animation="wave" />
                    <Skeleton width={42} height={12} animation="wave" sx={{ borderRadius: 0.5 }} />
                  </Box>
                </TableCell>
                <TableCell sx={tdSx}><Skeleton width={32} height={14} animation="wave" sx={{ borderRadius: 0.5 }} /></TableCell>
                {[0, 1, 2, 3].map((cellIndex) => (
                  <TableCell key={cellIndex} sx={tdSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Skeleton variant="circular" width={8} height={8} animation="wave" />
                      <Skeleton width={22} height={10} animation="wave" sx={{ borderRadius: 0.5 }} />
                    </Box>
                  </TableCell>
                ))}
                <TableCell sx={tdSx}><Skeleton width={32} height={12} animation="wave" sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell sx={tdSx}>
                  {rowIndex % 3 === 2 ? (
                    <Skeleton width={10} height={12} animation="wave" sx={{ borderRadius: 0.5 }} />
                  ) : (
                    <Skeleton variant="rounded" width={52} height={18} animation="wave" sx={{ borderRadius: 9 }} />
                  )}
                </TableCell>
                <TableCell sx={tdSx}>
                  {rowIndex % 4 === 3 ? (
                    <Skeleton width={10} height={12} animation="wave" sx={{ borderRadius: 0.5 }} />
                  ) : (
                    <Skeleton variant="rounded" width={58} height={18} animation="wave" sx={{ borderRadius: 9 }} />
                  )}
                </TableCell>
                <TableCell sx={tdSx}><Skeleton width={48} height={12} animation="wave" sx={{ borderRadius: 0.5 }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </Box>
);

export default LoadingSkeleton;
